import React from 'react';
import { translations } from '../translations';
import {Heart} from 'lucide-react';
import { footerLocationLinks, footerServiceLinks, footerGuideLinks, footerRatgeberLinks } from '../data/seoPages';
import { seoUiLabels } from '../data/seoPageTranslations';

interface FooterProps {
  currentLang?: 'de' | 'en' | 'es';
}

export const Footer: React.FC<FooterProps> = ({ currentLang = 'de' }) => {
  const t = translations[currentLang].footer;
  
  return (
    <footer className="relative bg-[#c9d8df] text-secondary">
      <div className="container mx-auto px-6 py-16 lg:py-20">
        
        {/* Main Footer Content - 5 SPALTEN PREMIUM LAYOUT */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-16">
          
          {/* Brand Column */}
          <div className="text-center lg:text-left">
            <div className="h-16 mb-6 mx-auto lg:mx-0 flex items-center justify-center lg:justify-start">
              <span className="font-serif text-3xl text-secondary font-bold tracking-wide">GEIS CARE</span>
            </div>
            <p className="text-secondary/80 text-sm leading-relaxed mb-6 max-w-xs mx-auto lg:mx-0">
              {t.tagline}
            </p>
            <div className="flex items-center justify-center lg:justify-start gap-2 text-sm text-secondary/60">
              <span>{t.madeWith}</span>
              <Heart className="w-4 h-4 text-[#A68B5B] fill-[#A68B5B] animate-pulse" />
              <span>{t.inMallorca}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="text-center lg:text-left">
            <h4 className="font-serif text-2xl text-secondary mb-6 relative inline-block">
              {t.quickLinks}
              <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent" />
            </h4>
            <div className="flex flex-col gap-3">
              <a href="/#about" className="text-secondary/70 hover:text-white transition-colors duration-300 text-base">{t.about}</a>
              <a href="/#services" className="text-secondary/70 hover:text-white transition-colors duration-300 text-base">{t.services}</a>
              <a href="/#testimonials" className="text-secondary/70 hover:text-white transition-colors duration-300 text-base">{t.testimonials}</a>
              <a href="/#contact" className="text-secondary/70 hover:text-white transition-colors duration-300 text-base">{t.contact}</a>
            </div>
          </div>

          {/* Kontakt & NAP Column */}
          <div className="text-center lg:text-left">
            <h4 className="font-serif text-2xl text-secondary mb-6 relative inline-block">
              {t.contactTitle}
              <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent" />
            </h4>
            <div className="flex flex-col gap-3 text-secondary/70 text-base">
              <div className="font-semibold text-secondary/90">Geis Care Mallorca</div>
              <div className="text-sm">
                <div>Carrer Sant Miquel 36</div>
                <div>07530 Sant Llorenç des Cardassar</div>
                <div>Mallorca, Spanien</div>
              </div>
              <a href="mailto:info@geis-care.com" className="hover:text-white transition-colors duration-300">
                info@geis-care.com
              </a>
              <a href="tel:+34601889566" className="hover:text-white transition-colors duration-300">
                +34 601 889 566
              </a>
              <a href="tel:+4915170800913" className="hover:text-white transition-colors duration-300">
                +49 151 708 009 13
              </a>
              <a href="https://wa.me/34601889566" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300">
                {t.whatsapp}
              </a>
              <div className="text-sm mt-2 text-secondary/60">
                {currentLang === 'de' ? 'Termine nach Vereinbarung' : currentLang === 'en' ? 'By appointment' : 'Con cita previa'}
              </div>
            </div>
          </div>

          {/* SEO Keywords */}
          <div className="text-center lg:text-left">
            <h4 className="font-serif text-2xl text-secondary mb-6 relative inline-block">
              {t.seoTitle}
              <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent" />
            </h4>
            <div className="flex flex-col gap-2">
              {t.seoKeywords?.map((keyword: string, i: number) => (
                <a
                  key={i}
                  href="/pflege-mallorca"
                  className="text-secondary/70 hover:text-white transition-colors duration-300 text-sm"
                >
                  {keyword}
                </a>
              ))}
            </div>
          </div>

          {/* Social & Legal */}
          <div className="text-center lg:text-left">
            <h4 className="font-serif text-2xl text-secondary mb-6 relative inline-block">
              {t.followUs}
              <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent" />
            </h4>
            
            {/* Social Icons */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-3 mb-8">
              <a 
                href="https://www.instagram.com/geis_care" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group w-10 h-10 sm:w-11 sm:h-11 aspect-square bg-white backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/80 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                aria-label="Instagram"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                  <defs>
                    <radialGradient id="instagramGradient" cx="30%" cy="107%" r="150%">
                      <stop offset="0%" stopColor="#FDF497" />
                      <stop offset="45%" stopColor="#FD5949" />
                      <stop offset="60%" stopColor="#D6249F" />
                      <stop offset="90%" stopColor="#285AEB" />
                    </radialGradient>
                  </defs>
                  <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#instagramGradient)"/>
                  <circle cx="12" cy="12" r="4" stroke="#333333" strokeWidth="2" fill="none"/>
                  <circle cx="17.5" cy="6.5" r="1.5" fill="#333333"/>
                </svg>
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61584125741556" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group w-10 h-10 sm:w-11 sm:h-11 aspect-square bg-white backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/80 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                aria-label="Facebook"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" className="group-hover:scale-110 transition-transform duration-300">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://wa.me/34601889566" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group w-10 h-10 sm:w-11 sm:h-11 aspect-square bg-white backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/80 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                aria-label="WhatsApp"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366" className="group-hover:scale-110 transition-transform duration-300">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
              <a 
                href="https://share.google/6YmddJoOe8ILnWefu" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group w-10 h-10 sm:w-11 sm:h-11 aspect-square bg-white backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/80 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                aria-label="Google Business"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </a>
              <a 
                href="https://www.tiktok.com/@geiscare" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group w-10 h-10 sm:w-11 sm:h-11 aspect-square bg-white backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/80 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                aria-label="TikTok"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                  <defs>
                    <linearGradient id="tiktokGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#EE1D52" />
                      <stop offset="50%" stopColor="#69C9D0" />
                      <stop offset="100%" stopColor="#EE1D52" />
                    </linearGradient>
                  </defs>
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" fill="url(#tiktokGradient)"/>
                </svg>
              </a>
              <a 
                href="https://www.youtube.com/@geiscare" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group w-10 h-10 sm:w-11 sm:h-11 aspect-square bg-white backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/80 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                aria-label="YouTube"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
                </svg>
              </a>
            </div>

            {/* Legal Links */}
            <div className="flex flex-col items-center lg:items-start gap-3 text-base">
              <a href="/impressum" className="text-secondary/70 hover:text-white transition-colors duration-300 cursor-pointer">{t.impressum}</a>
              <a href="/datenschutz" className="text-secondary/70 hover:text-white transition-colors duration-300 cursor-pointer">{t.privacy}</a>
              <button 
                onClick={() => {
                  if ((window as any).openCookieSettings) {
                    (window as any).openCookieSettings();
                  }
                }}
                className="text-secondary/70 hover:text-white transition-colors duration-300"
              >
                {t.cookieSettings}
              </button>
            </div>
          </div>

        </div>

        {/* SEO Location & Service Links */}
        <div className="border-t border-secondary/10 pt-10 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Service Pages */}
            <div>
              <h5 className="font-serif text-lg text-secondary mb-4 font-semibold">{seoUiLabels[currentLang || 'de'].footerServiceHeading}</h5>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {footerServiceLinks.map((link, i) => (
                  <a
                    key={i}
                    href={`/${link.slug}`}
                    className="text-secondary/60 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.labels[currentLang || 'de']}
                  </a>
                ))}
              </div>
            </div>
            {/* Location Pages */}
            <div>
              <h5 className="font-serif text-lg text-secondary mb-4 font-semibold">{seoUiLabels[currentLang || 'de'].footerLocationHeading}</h5>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {footerLocationLinks.map((link, i) => (
                  <a
                    key={i}
                    href={`/${link.slug}`}
                    className="text-secondary/60 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.labels[currentLang || 'de']}
                  </a>
                ))}
              </div>
            </div>
            {/* Guide Pages */}
            <div>
              <h5 className="font-serif text-lg text-secondary mb-4 font-semibold">{seoUiLabels[currentLang || 'de'].footerGuideHeading}</h5>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {footerGuideLinks.map((link, i) => (
                  <a
                    key={i}
                    href={`/guide/${link.slug}`}
                    className="text-secondary/60 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.labels[currentLang || 'de']}
                  </a>
                ))}
              </div>
            </div>
            {/* Ratgeber Pages */}
            <div>
              <h5 className="font-serif text-lg text-secondary mb-4 font-semibold">{seoUiLabels[currentLang || 'de'].footerRatgeberHeading}</h5>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {footerRatgeberLinks.map((link, i) => (
                  <a
                    key={i}
                    href={`/ratgeber/${link.slug}`}
                    className="text-secondary/60 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.labels[currentLang || 'de']}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 mt-8 border-t border-secondary/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-secondary/50">
            <div>
              &copy; 2026 Geis Care. {t.rights}
            </div>
            <div className="flex items-center gap-2">
              <span>{t.developedWith}</span>
              <Heart className="w-4 h-4 text-[#A68B5B] fill-[#A68B5B]" />
              <span>{t.forCare}</span>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};
